import { Query } from '@apollo/client/react/components';
import moment from 'moment';
import { useState } from 'react';
import { Helmet } from 'react-helmet';

import {
  HomeBannerConnectCRM,
  HomeBannerUpgradePlan,
} from '~/components/home/Banners';
import { TimePeriodContext } from '~/components/home/context/TimePeriodContext';
import { HomeFooter } from '~/components/home/HomeFooter';
import { HomeHeader } from '~/components/home/HomeHeader';
import {
  HomeSectionDataOverview,
  HomeSectionImplementation,
  HomeSectionMatchRate,
  HomeSectionNotifications,
} from '~/components/home/HomeSection';
import {
  HomeSidebar,
  HomeSidebarNews,
  HomeSidebarNextSteps,
  HomeSidebarUsage,
} from '~/components/home/HomeSidebar';
import useOnboardingStatus from '~/hooks/account/useOnboardingStatus';

import homeQuery from './homeQuery.graphql';
import { DashboardProps } from './types';

export const HomePageContent = ({ data, loading }: DashboardProps) => {
  const { hasConnectors, shouldOnboard } = useOnboardingStatus();
  const isOnboarding = shouldOnboard;

  const [timePeriod, setTimePeriod] = useState('60');

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <div className="w-full xl:grid grid-cols-[1fr_25rem]">
        <div className="min-w-0 p-8 ck-scrollbar ck-scrollbar--vertical space-y-12">
          <HomeHeader
            account={data?.account}
            hasAccountIssue={!!data?.accountNotifications.length}
            isOnboarding={isOnboarding}
            user={data?.viewer.user}
          />
          <HomeSectionNotifications
            accountConnectors={data?.accountConnectors}
            notifications={data?.accountNotifications}
          />
          {isOnboarding ? <HomeSectionImplementation /> : null}
          <div className="relative space-y-12">
            {!hasConnectors ? <HomeBannerConnectCRM /> : null}
            {!isOnboarding ? <HomeBannerUpgradePlan /> : null}
            <TimePeriodContext.Provider value={{ timePeriod, setTimePeriod }}>
              <HomeSectionDataOverview
                data={data?.rollupCounts}
                isLoading={loading}
              />
            </TimePeriodContext.Provider>
            <HomeSectionMatchRate className="pt-12 border-t border-gray-200" />
          </div>
          <HomeFooter />
        </div>
        <HomeSidebar>
          <HomeSidebarUsage />
          {!isOnboarding ? <HomeSidebarNextSteps /> : null}
          <HomeSidebarNews news={data?.news} />
        </HomeSidebar>
      </div>
    </>
  );
};

export const HomePage = () => {
  const momentFormat = 'YYYY-MM-DD';
  const start = moment()
    .subtract(60, 'days')
    .format(momentFormat);
  const end = moment().format(momentFormat);

  return (
    <Query
      fetchPolicy="network-only"
      query={homeQuery}
      variables={{ start, end }}
    >
      {(props: any) => <HomePageContent {...props} />}
    </Query>
  );
};
